/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import * as codec from 'purify-ts/Codec';
import * as api from '@codegrade/api';
import { assert, itertools, typeHelpers } from '@codegrade/utils';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function decode(decoder, data) {
    return decoder.unsafeDecode(data);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class HandinRequirements {
    /* eslint-disable @typescript-eslint/no-use-before-define */
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromServerData(maybeVersion, maybeData) {
        return maybeData.caseOf({
            Just: data => maybeVersion.caseOf({
                Just: version => {
                    switch (version) {
                        case 'EmptySubmissionFilter':
                            return new EmptySubmissionFilter(decode(codec.exactly(''), data));
                        case 'IgnoreFilterManager':
                            return new IgnoreFilterManager(decode(codec.string, data));
                        case 'SubmissionValidator':
                            if (data instanceof api.SubmissionValidatorInputData) {
                                return new SubmissionValidator(data);
                            }
                            else {
                                return new SubmissionValidator(decode(api.SubmissionValidatorInputData, data));
                            }
                        default:
                            return assert.AssertionError.assertNever(version);
                    }
                },
                Nothing: () => new IgnoreFilterManager(decode(codec.string, data)),
            }),
            Nothing: () => {
                const version = maybeVersion.orDefault('EmptySubmissionFilter');
                assert.AssertionError.assert(version === 'EmptySubmissionFilter', 'Got a CGIgnore version but no data.');
                return new EmptySubmissionFilter('');
            },
        });
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class EmptySubmissionFilter extends HandinRequirements {
    data;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    type = 'EmptySubmissionFilter';
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(data) {
        super();
        this.data = data;
        this.data = data;
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getServerData() {
        return {
            ignore: '',
            ignoreVersion: this.type,
        };
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    isEqual(other) {
        if (other.type !== this.type) {
            return false;
        }
        return true;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class IgnoreFilterManager extends HandinRequirements {
    data;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    type = 'IgnoreFilterManager';
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(data) {
        super();
        this.data = data;
        this.data = data;
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getServerData() {
        return {
            ignore: this.data,
            ignoreVersion: this.type,
        };
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    isEqual(other) {
        if (other.type !== this.type) {
            return false;
        }
        return this.data === other.data;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function rulesAreEqual(ruleA, ruleB) {
    return (ruleA.fileType === ruleB.fileType &&
        ruleA.name === ruleB.name &&
        ruleA.ruleType === ruleB.ruleType);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function optionsAreEqual(optionA, optionB) {
    return optionA.key === optionB.key && optionA.value === optionB.value;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class SubmissionValidator extends HandinRequirements {
    data;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    type = 'SubmissionValidator';
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(data) {
        super();
        this.data = data;
        Object.freeze(data);
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getServerData() {
        return {
            ignoreVersion: this.type,
            ignore: this.data,
        };
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    isEqual(other) {
        if (other.type !== this.type) {
            return false;
        }
        return typeHelpers
            .keysOfSlow(['policy', 'rules', 'options'])
            .every(element => {
            switch (element) {
                case 'policy':
                    return this.data.policy === other.data.policy;
                case 'rules': {
                    if (this.data.rules.length !== other.data.rules.length) {
                        return false;
                    }
                    return itertools.all(itertools.zip(this.data.rules, other.data.rules), ([ruleA, ruleB]) => rulesAreEqual(ruleA, ruleB));
                }
                case 'options': {
                    if (this.data.options.length !== other.data.options.length) {
                        return false;
                    }
                    return itertools.all(itertools.zip(this.data.options, other.data.options), ([optionA, optionB]) => optionsAreEqual(optionA, optionB));
                }
                default:
                    return assert.AssertionError.assertNever(element);
            }
        });
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class HandinRequirementsOption extends api.OptionsInputData {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    id;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    name;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    description;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    options;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(data) {
        super({ key: data.key, value: data.value });
        this.id = data.id;
        this.name = data.name;
        this.description = data.description;
        this.options = data.options;
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    updateValue(value) {
        return new HandinRequirementsOption({ ...this, value });
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class HandinRequirementRule extends api.FileRuleInputData {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    removed;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    editing;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    focus;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(data, 
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    opts) {
        super(data);
        this.removed = opts.removed ?? false;
        this.editing = opts.editing ?? false;
        this.focus = opts.focus ?? false;
    }
}
